import { useState, useEffect, useMemo } from "react";
import * as api from "../../api";
import { isUnregisteredBank } from "../../lib/reviewReasons";

/* F3B-6c — Formulario de pago + wallet + submit extraído VERBATIM desde
 * App.js como UN SOLO BLOQUE. remainingAfterCreditBs y su auto-sync viajan
 * juntos. handleSubmitPayment sin ningun cambio de logica: gates
 * AMOUNT_MISMATCH (proofFile se conserva para el retry con la MISMA imagen),
 * DUPLICATE_REFERENCE, WRONG_DESTINATION y needs_manual_reference intactos. */
export function usePaymentSubmit({
  user, setUser, orders, setOrders, cart, setCart, cartTotalBs,
  exchangeRate, currency, paymentMethods, showToast,
}) {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [clientCi, setClientCi] = useState("");
  const [selectedPmId, setSelectedPmId] = useState("");
  // Category tab inside the checkout modal (client-facing): PAGO_MOVIL | TRANSFERENCIA | USD_PLATFORM
  const [checkoutPmTab, setCheckoutPmTab] = useState("PAGO_MOVIL");

  // Smart pre-selection: when the payment modal opens or the user switches currency,
  // default to the PM category matching the active currency (USD → USD_PLATFORM, Bs → PAGO_MOVIL/TRANSFERENCIA).
  useEffect(() => {
    if (!showPaymentModal) return;
    const active = paymentMethods?.filter(pm => pm.active !== false);
    const has = (t) => active?.some(pm => pm.type === t);
    if (currency === "USD" && has("USD_PLATFORM")) {
      setCheckoutPmTab("USD_PLATFORM");
    } else if (has("PAGO_MOVIL")) {
      setCheckoutPmTab("PAGO_MOVIL");
    } else if (has("TRANSFERENCIA")) {
      setCheckoutPmTab("TRANSFERENCIA");
    } else if (has("USD_PLATFORM")) {
      setCheckoutPmTab("USD_PLATFORM");
    }
  }, [showPaymentModal, currency, paymentMethods]);

  // When the PM tab changes while the modal is open, reset the "paid amount" field so the
  // user doesn't accidentally submit a Bs numeral as USD (or vice versa). The placeholder
  // still shows the correct expected total in the active currency.
  useEffect(() => {
    if (showPaymentModal) {
      setPaidAmountBs("");
    }
  }, [checkoutPmTab, showPaymentModal]);

  const [paidAmountBs, setPaidAmountBs] = useState("");
  // Manual-reference fallback (only shown when the AI could not read the reference
  // from the uploaded proof). Empty otherwise — the AI-extracted value is the
  // primary anti-reuse fingerprint stored server-side in `ai_reference`.
  const [referenceNumber, setReferenceNumber] = useState("");
  const [needsManualRef, setNeedsManualRef] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [proofFileName, setProofFileName] = useState("");
  const [useAccountCredit, setUseAccountCredit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  // Derived amount the user still needs to transfer once their in-store credit
  // is applied. Memoised so the auto-sync effect below only re-runs when the
  // *result* changes (not each time an unrelated dep re-renders).
  const remainingAfterCreditBs = useMemo(() => {
    const availableCreditBs = Math.max(0, Number(user?.credit_balance_bs) || 0);
    if (!useAccountCredit || availableCreditBs <= 0) return null; // sentinel: don't auto-fill
    const creditAppliedBs = Math.min(availableCreditBs, cartTotalBs);
    return Math.max(0, cartTotalBs - creditAppliedBs);
  }, [useAccountCredit, cartTotalBs, user?.credit_balance_bs]);

  // Auto-sync paid amount when the "Usar saldo a favor" toggle is on: reflects the
  // remaining amount the user still needs to transfer (0 when credit fully covers).
  useEffect(() => {
    if (!showPaymentModal || remainingAfterCreditBs === null) return;
    if (checkoutPmTab === "USD_PLATFORM") {
      const rate = Number(exchangeRate) || 1;
      setPaidAmountBs(remainingAfterCreditBs > 0 ? (remainingAfterCreditBs / rate).toFixed(2) : "0");
    } else {
      setPaidAmountBs(String(Math.round(remainingAfterCreditBs)));
    }
  }, [remainingAfterCreditBs, checkoutPmTab, showPaymentModal, exchangeRate]);

  // Result modal
  const [paymentResultModal, setPaymentResultModal] = useState(false);
  const [lastOrderResult, setLastOrderResult] = useState(null);
  const [previewProofUrl, setPreviewProofUrl] = useState(null);

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    setValidationErrors([]);
    if (cart.length === 0) {
      showToast("Carrito Vacío", "Agrega al menos un producto", "error");
      return;
    }
    // La moneda del pago la define el TIPO REAL del método seleccionado, no
    // la pestaña visible: si quedaban desincronizadas, un monto en bolívares
    // se multiplicaba por la tasa y se persistía inflado ×850.
    const selectedPm = (paymentMethods || []).find(pm => pm.id === selectedPmId);
    const isUsdPayment = (selectedPm?.type || checkoutPmTab) === "USD_PLATFORM";
    // Decide credit application up front so we know whether the user still owes anything.
    const availableCreditBs = Math.max(0, Number(user.credit_balance_bs) || 0);
    const creditAppliedBs = useAccountCredit ? Math.min(availableCreditBs, cartTotalBs) : 0;
    const fullyCoveredByCredit = useAccountCredit && creditAppliedBs >= cartTotalBs - 0.5;

    // Validation branches
    if (!fullyCoveredByCredit) {
      if (!paidAmountBs || !proofFile) {
        showToast("Faltan Datos", "Completa el monto y sube el comprobante", "error");
        return;
      }
      // Manual reference is only required as a fallback AFTER the AI failed to
      // read it (needsManualRef becomes true when the backend returns 422).
      if (needsManualRef && !isUsdPayment && !referenceNumber) {
        showToast("Faltan Datos", "Escribe la referencia del comprobante para que el admin la revise", "error");
        return;
      }
    }
    // If the client paid via USD platform the input is in dollars — convert to Bs before sending
    // so the backend receives a consistent value in the store's base currency.
    const paidBsToSend = fullyCoveredByCredit
      ? 0
      : isUsdPayment
        ? Number(paidAmountBs) * Number(exchangeRate)
        : parseFloat(paidAmountBs);
    setSubmitting(true);
    try {
      const res = await api.createCartOrders({
        items: cart?.map(it => ({
          gameId: it.gameId,
          packageId: it.packageId,
          playerId: it.playerId,
          playerPassword: it.playerPassword,
          playerServer: it.playerServer || null,
          accessMethod: it.accessMethod || null,
          verifiedPlayerName: it.verifiedPlayerName || null,
        })),
        clientCi: "",
        paymentMethodId: fullyCoveredByCredit ? "" : selectedPmId,
        paidAmountBs: paidBsToSend,
        referenceNumber: (fullyCoveredByCredit || isUsdPayment) ? "" : referenceNumber,
        useCredit: useAccountCredit,
        proofFile: fullyCoveredByCredit ? null : proofFile,
        quotedExchangeRate: exchangeRate,
      });
      // Merge new orders at the top
      setOrders([...res.orders, ...orders]);
      if (res.new_credit_balance_bs !== undefined) {
        setUser(u => u ? { ...u, credit_balance_bs: res.new_credit_balance_bs } : u);
      }
      // Wrap result in a shape compatible with the result modal (uses lastOrderResult.order.id)
      setLastOrderResult({
        ...res,
        order: res.orders[0], // for backward compat with the modal
        cart_size: res.orders.length,
      });
      setShowPaymentModal(false);
      setPaymentResultModal(true);
      // FASE 4 — un banco desconocido NO es un comprobante inválido: la orden
      // se creó en NEEDS_REVIEW y el aviso al cliente debe ser informativo.
      if (res.verification_status === "NEEDS_REVIEW") {
        const unregistered = isUnregisteredBank(res.review_reason);
        showToast(
          unregistered ? "Banco no registrado" : "Pedido en revisión",
          unregistered
            ? "Recibimos tu comprobante. Un agente verificará el banco manualmente."
            : "Un agente revisará tu comprobante en unos minutos.",
          "info"
        );
      }
      // Clear cart + form
      setCart([]);
      setClientCi(""); setPaidAmountBs(""); setReferenceNumber("");
      setNeedsManualRef(false);
      setProofFile(null); setProofFileName(""); setUseAccountCredit(false);
    } catch (err) {
      // Tienda cerrada mientras la página seguía abierta: el backend bloquea el
      // pedido (423) y avisamos a App para refrescar el estado ABIERTA/CERRADA.
      if (err?.status === 423) {
        window.dispatchEvent(new Event("ag7:site-status-changed"));
      }
      // Los fallos de infraestructura no describen el comprobante ni el
      // monto del cliente. Conservamos el formulario para que pueda reintentar.
      if (err?.status >= 500 || err?.status === 502 || err?.status === 503 || err?.status === 504) {
        setValidationErrors([]);
        showToast(
          "No pudimos registrar el pago",
          "Tuvimos un inconveniente temporal. Tu comprobante no fue rechazado; espera unos segundos y vuelve a intentarlo.",
          "error"
        );
      // Structured backend error? (new sync-OCR gates: AMOUNT_MISMATCH,
      // DUPLICATE_REFERENCE, WRONG_DESTINATION). `err.data.detail` is an
      // object with `code` when the backend used our gate response shape.
      } else {
      const detail = err?.data?.detail;
      const structured = detail && typeof detail === "object" && detail.code;
      if (structured) {
        const gateCode = detail.code;
        if (gateCode === "CURRENCY_MISMATCH") {
          setValidationErrors([detail.message, ...(detail.how_to_fix || [])]);
          showToast(
            "Comprobante en Bolívares",
            "Elegiste un método de pago en $. Cambia a Pago Móvil/Transferencia o sube un comprobante en dólares.",
            "error"
          );
        } else if (gateCode === "AMOUNT_MISMATCH") {
          // Prefill the paid-amount input with the detected value so the user
          // only needs to tap Confirmar again — same image hits the OCR cache.
          const detectedUsd = Number(detail.detected_amount_usd || 0);
          const detectedBs = Number(detail.detected_amount_bs || 0);
          const isUsdGate = detail.currency === "USD" || detectedUsd > 0;
          if (isUsdGate) {
            if (detectedUsd > 0) setPaidAmountBs(String(detectedUsd));
            setValidationErrors([detail.message]);
            showToast(
              "Monto no coincide",
              `El comprobante muestra $${detectedUsd.toFixed(2)}. Corregimos el campo por ti; presiona Confirmar de nuevo.`,
              "error"
            );
          } else {
            if (detectedBs > 0 && !isUsdPayment) setPaidAmountBs(String(detectedBs));
            setValidationErrors([detail.message]);
            showToast(
              "Monto no coincide",
              `El comprobante muestra Bs ${detectedBs.toFixed(2)}. Corregimos el campo por ti; presiona Confirmar de nuevo.`,
              "error"
            );
          }
        } else if (gateCode === "DUPLICATE_REFERENCE") {
          setValidationErrors([detail.message]);
          showToast(
            "Comprobante ya usado",
            detail.message,
            "error"
          );
        } else if (gateCode === "WRONG_DESTINATION") {
          const parts = (detail.wrong_fields || []).map(f => {
            const label = f === "cedula" ? "cédula" : f === "telefono" ? "teléfono" : f;
            const got = detail.detected?.[f] || "—";
            const exp = detail.expected?.[f] || "—";
            return `${label}: comprobante="${got}" vs cuenta AG7="${exp}"`;
          });
          setValidationErrors([detail.message, ...parts]);
          showToast(
            "Pago no dirigido a AG7 Store",
            "Los datos de destino no coinciden. Revisa que hayas transferido a la cuenta correcta.",
            "error"
          );
        } else if (gateCode === "BANK_NOT_REGISTERED" || gateCode === "UNREGISTERED_BANK") {
          // Defensivo: el motor deriva estos casos a NEEDS_REVIEW y NO
          // devuelve error. Si algún día llegara como error, el cliente
          // debe ver un aviso informativo, jamás "comprobante inválido".
          setValidationErrors([]);
          showToast(
            "Banco no registrado",
            "El banco del comprobante no está en nuestra lista. Un agente lo revisará manualmente.",
            "info"
          );
        } else {
          setValidationErrors([detail.message || err.message]);
          showToast("Comprobante Inválido", detail.message || err.message, "error");
        }
      } else if (err.data?.code === "needs_manual_reference") {
        // The AI couldn't read the reference from the proof. Ask the user
        // to type it manually; the resubmit will flag it for admin review.
        setNeedsManualRef(true);
        setValidationErrors([]);
        showToast(
          "Referencia no legible",
          "No pudimos leer la referencia. Escríbela manualmente y toca Confirmar de nuevo.",
          "info"
        );
      } else {
        const errs = err.data?.validation_errors || [err.message];
        setValidationErrors(errs);
        showToast("Comprobante Inválido", errs[0] || "Verifica los datos del pago", "error");
      }
      }
    } finally {
      setSubmitting(false);
    }
  };

  return {
    showPaymentModal, setShowPaymentModal,
    clientCi, setClientCi, selectedPmId, setSelectedPmId,
    checkoutPmTab, setCheckoutPmTab,
    paidAmountBs, setPaidAmountBs, referenceNumber, setReferenceNumber,
    needsManualRef, setNeedsManualRef,
    proofFile, setProofFile, proofFileName, setProofFileName,
    useAccountCredit, setUseAccountCredit,
    submitting, validationErrors, setValidationErrors,
    remainingAfterCreditBs,
    paymentResultModal, setPaymentResultModal,
    lastOrderResult, setLastOrderResult,
    previewProofUrl, setPreviewProofUrl,
    handleSubmitPayment,
  };
}
